<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import LoadingDots from "$lib/components/LoadingDots/LoadingDots.svelte";
  import { linkTo } from "$lib/utils/url";
  /**
   * Is this the principal call to action on the page?
   */
  export let primary: boolean = false;

  export let primaryInvert: boolean = false;

  export let neutral: boolean = false;
  /**
   * What background color to use
   */
  export let backgroundColor: string | false = false;

  /**
   * What hover background color to use
   */
  export let hoverBgColor: string | false = false;
  /**
   * Button contents
   */
  export let label: string = "";
  export let caption: string = "";
  /**
   * Button disabled state
   */
  export let disabled: boolean = false;
  /**
   * Optional button anchor link
   */
  export let href: string | false = false;
  /**
   * Should anchor open in new tab?
   */
  export let newTab: boolean = false;
  /**
   * Icon to be displayed in button
   */
  export let icon: string | false = false;
  /**
   * Icon to the left (true) or right (false)
   */
  export let iconLeft: boolean = false;
  /**
   * Only display text/icon, no button style
   */
  export let plainText: boolean = false;

  export let fullWidth: boolean = false;

  export let submit: boolean = false;

  export let theme: string = "inverse-on-surface";

  export let hoverTheme: string = "on-surface";

  export let height = "40px";

  export let padding = "9px 24px 7px 24px";

  export let iconOnly: boolean = false;

  export let iconStyle: string = "rounded";

  export let loading: boolean = false;

  export let hoverDisabled: boolean = false;

  let plainTextStyle = plainText ? "plain-text" : "";

  let mode = hoverDisabled
    ? "button--no-hover"
    : primary
      ? "button--primary"
      : neutral
        ? "button--neutral"
        : "button--secondary";

  $: invert = primary && primaryInvert ? "button--primary__invert" : "";
  $: label = label;

  let fullWidthStyle = fullWidth ? "button--fw" : "";

  const dispatch = createEventDispatcher();

  let hovered: boolean = false;

  let style;
  $: style = `${
    backgroundColor ? `background-color: ${backgroundColor}` : ""
  }; ${hovered && hoverBgColor ? `background-color: ${hoverBgColor}` : ""}; ${
    height ? `height: ${height}` : ""
  }; ${padding ? `padding: ${padding}` : ""}`;

  /**
   * Optional click handler
   */
  function onClick(event: Event) {
    dispatch("click", event);
  }

  $: if (disabled) {
    hovered = false;
  }
</script>

<style lang="scss">
  a {
    width: max-content;
  }

  .button {
    cursor: pointer;
    text-transform: uppercase;
    transition: 0.2s all ease-in-out;
    text-decoration: none;
    border-radius: 20px;
    display: flex;
    justify-content: center;
    align-items: center;

    &--fw {
      width: 100%;
      max-width: 100%;
    }

    &--no-hover {
      color: $inverse-on-surface;
      background-color: $inverse-surface;
      border: 1px solid transparent;
    }
    &--primary {
      color: $inverse-on-surface;
      background-color: $inverse-surface;
      border: 1px solid transparent;

      &:hover {
        color: $on-surface;
        background-color: transparent;
        border: 1px solid $on-surface;
      }

      &:disabled {
        pointer-events: none;
        background-color: $on-surface-30;
        color: $inverse-on-surface;
      }

      &__invert {
        color: $on-surface;
        background-color: $surface;

        &:hover {
          color: $inverse-on-surface;
          background-color: transparent;
          border: 1px solid $inverse-on-surface;
        }

        &:disabled {
          background-color: $inverse-on-surface-30;
          color: $on-surface;
        }
      }
    }

    &--secondary {
      color: $on-surface;
      background-color: transparent;
      border: 1px solid $on-surface;

      &:hover {
        color: $inverse-on-surface;
        background-color: $inverse-surface;
        border: 1px solid $inverse-surface;
      }

      &:disabled {
        pointer-events: none;
        border-color: $on-surface-30;
        background-color: $surface;

        :global(span.button1) {
          color: $on-surface;
        }
      }
    }

    &--neutral {
      color: $on-surface;
      background-color: $on-surface-10;
      border: 1px solid transparent;

      &:hover {
        color: $on-surface;
        background-color: $inverse-on-surface;
        border: 1px solid $on-surface-10;
      }
    }
  }

  .plain-text {
    background-color: transparent;
    padding: 0;
    border-radius: 0px;
    border: none;
  }

  .icon {
    &--left {
      padding-right: 14px;
    }

    &--right {
      padding-left: 14px;
      &__neutral {
        padding-left: 8px;
      }
    }

    &--only {
      padding: 0;
    }
  }
</style>

{#if href}
  <a
    on:mouseenter={() => (hovered = true)}
    on:mouseleave={() => (hovered = false)}
    href={linkTo(href)}
    class={["button", mode, invert, plainTextStyle, fullWidthStyle].join(" ")}
    {style}
    {disabled}
    target={newTab ? "_blank" : null}
    rel="noopener noreferrer"
  >
    {#if iconLeft && icon}
      <Icon {icon} {iconStyle} color={hovered ? hoverTheme : theme} />
    {/if}
    {#if label}
      <Typography
        component="span"
        variant="button1"
        theme={hovered
          ? hoverTheme
          : disabled
            ? primary
              ? primaryInvert
                ? "on-surface"
                : "inverse-on-surface"
              : "on-surface-30"
            : neutral
              ? "on-surface"
              : theme}
      >
        {label}
      </Typography>
    {/if}
    {#if loading}
      <LoadingDots />
    {/if}
    {#if !iconLeft && icon}
      <Icon {icon} {iconStyle} color={hovered ? hoverTheme : theme} />
    {/if}
  </a>
{:else}
  <button
    on:mouseenter={() => (hovered = true)}
    on:mouseleave={() => (hovered = false)}
    type={submit ? "submit" : "button"}
    class={["button", mode, invert, plainTextStyle, fullWidthStyle].join(" ")}
    {disabled}
    {style}
    on:click={e => {
      onClick(e);
    }}
  >
    {#if iconLeft && icon}
      <div class="icon--left">
        <Icon {icon} {iconStyle} color={hovered ? hoverTheme : theme} />
      </div>
    {/if}
    {#if label || caption}
      <Typography
        component="span"
        variant="button1"
        theme={hoverDisabled
          ? "inverse-on-surface"
          : hovered
            ? hoverTheme
            : disabled
              ? primary
                ? primaryInvert
                  ? "on-surface"
                  : "inverse-on-surface"
                : "on-surface-30"
              : neutral
                ? "on-surface"
                : theme}
      >
        {label}
        {#if caption}
          <Typography
            variant="caption1"
            component="span"
            theme={hovered
              ? hoverTheme
              : disabled
                ? primary
                  ? primaryInvert
                    ? "on-surface"
                    : "inverse-on-surface"
                  : "on-surface-30"
                : neutral
                  ? "on-surface"
                  : theme}
          >
            {caption}
          </Typography>
        {/if}
      </Typography>
    {/if}
    {#if loading}
      <LoadingDots />
    {/if}
    {#if !iconLeft && icon}
      {#if iconOnly}
        <div class="icon--only">
          <Icon {icon} {iconStyle} color={hovered ? hoverTheme : theme} />
        </div>
      {:else if !neutral}
        <div class="icon--right">
          <Icon {icon} {iconStyle} color={hovered ? hoverTheme : theme} />
        </div>
      {:else}
        <div class="icon--right__neutral">
          <Icon {icon} {iconStyle} color={hovered ? hoverTheme : theme} />
        </div>
      {/if}
    {/if}
  </button>
{/if}
